import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import {
  useLead,
  useLeadActivities,
  useMasterSources,
  useLeadStatuses,
  useLeadMutations
} from '../../../hooks/useLeadQueries';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  ArrowLeft,
  Phone,
  Mail,
  MessageSquare,
  Calendar,
  MapPin,
  Building,
  User,
  Tag,
  Clock,
  Target,
  AlertCircle,
  FileText,
  Linkedin,
  Send,
  X,
  Plus,
  Link as LinkIcon,
  Edit,
  Paperclip
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import {
  sanitizeTextInput,
  sanitizeSearchInput,
  handleTextInputChange,
  getCharacterCount,
  FIELD_LIMITS
} from '../../../utils/inputValidation';
import { formatIndianNumber } from '../../../utils/amountFormatter';
import stringUtils from '../../../utils/stringUtils';
import PermissionGate from '../../../components/common/PermissionGate';
import { toast } from 'react-hot-toast';

// Map backend source to frontend display (directly populate from backend resolved values)
const mapSourceToDisplay = (source, masterSources = []) => {
  if (!source) return 'Manual Entry';

  // 1. If source is an object (already populated)
  if (source && typeof source === 'object') {
    return source.name || source.label || source.id || 'Manual Entry';
  }

  return source;
};

// Helper function to map employee count to company size display
const mapEmployeeCountToCompanySize = (employeeCount) => {
  if (!employeeCount) return 'Not specified';
  if (employeeCount <= 10) return 'Startup (1-10 employees)';
  if (employeeCount <= 50) return 'Small (10-50 employees)';
  if (employeeCount <= 200) return 'Medium (50-200 employees)';
  if (employeeCount <= 1000) return 'Large (200-1000 employees)';
  return 'Enterprise (1000+ employees)';
};

// Helper function to get lead name
const getLeadName = (lead) => {
  if (!lead) return 'Unknown Lead';
  if (lead.firstName && lead.lastName) {
    return `${lead.firstName} ${lead.lastName}`;
  } else if (lead.firstName) {
    return lead.firstName;
  } else if (lead.lastName) {
    return lead.lastName;
  } else {
    return 'Unknown Lead';
  }
};

// Helper function to get the correct activity time
const getActivityTime = (activity) => {
  if (activity.performedAt) {
    return new Date(activity.performedAt).toISOString();
  }

  if (activity.isAutomated && activity.callMetadata) {
    const callTime = activity.callMetadata.startTime ||
      activity.callMetadata.legBStartTime ||
      activity.callMetadata.legAPickedTime ||
      activity.callMetadata.legBPickedTime;

    if (callTime) {
      try {
        const date = new Date(callTime);
        if (!isNaN(date.getTime())) {
          return date.toISOString();
        }
      } catch (error) {

      }
    }
  }

  return activity.createdAt || new Date().toISOString();
};

// Helper function to format date with seconds
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

const getScoreColor = (score) => {
  switch (score) {
    case 'Hot': return 'bg-red-100 text-red-800';
    case 'Warm': return 'bg-yellow-100 text-yellow-800';
    case 'Cold': return 'bg-blue-100 text-blue-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getStatusColor = (status, masterStatuses = []) => {
  const normalizedStatus = String(status || '').toLowerCase();

  // Find in master statuses, prioritizing value matching first
  let statusObj = masterStatuses.find(s => s.value && s.value.toLowerCase() === normalizedStatus);
  if (!statusObj) {
    statusObj = masterStatuses.find(s => s.name && s.name.toLowerCase() === normalizedStatus);
  }

  const colorKey = statusObj ? (statusObj.value || statusObj.name).toLowerCase() : normalizedStatus;

  switch (colorKey) {
    case 'new': return 'bg-blue-100 text-blue-800';
    case 'contacted': return 'bg-green-100 text-green-800';
    case 'qualified': return 'bg-purple-100 text-purple-800';
    case 'converted': return 'bg-indigo-100 text-indigo-800';
    case 'disqualified': return 'bg-red-100 text-red-800';
    case 'proposal_sent': return 'bg-orange-100 text-orange-800';
    case 'negotiation': return 'bg-yellow-100 text-yellow-800';
    case 'closed_won': return 'bg-emerald-100 text-emerald-800';
    case 'closed_lost': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getStatusDisplay = (status, masterStatuses = []) => {
  const normalizedStatus = String(status || '').toLowerCase();
  
  // Find in master statuses, prioritizing value matching first
  let statusObj = masterStatuses.find(s => s.value && s.value.toLowerCase() === normalizedStatus);
  if (!statusObj) {
    statusObj = masterStatuses.find(s => s.name && s.name.toLowerCase() === normalizedStatus);
  }

  if (statusObj) return statusObj.name;

  if (normalizedStatus === 'closed_won') return 'Won';
  if (normalizedStatus === 'closed_lost') return 'Lost';
  return status;
};

const getActivityIcon = (type) => {
  switch (type) {
    case 'call': return Phone;
    case 'email': return Mail;
    case 'whatsapp': return MessageSquare;
    case 'meeting': return Calendar;
    case 'linkedin': return Linkedin;
    default: return Clock;
  }
};

const getActivityColor = (type) => {
  switch (type) {
    case 'call': return 'text-[#0F2942]';
    case 'email': return 'text-green-600';
    case 'whatsapp': return 'text-green-500';
    case 'meeting': return 'text-purple-600';
    case 'linkedin': return 'text-sky-700';
    default: return 'text-gray-600';
  }
};

const getActivityBgColor = (type) => {
  switch (type) {
    case 'call': return 'bg-blue-100';
    case 'email': return 'bg-green-100';
    case 'whatsapp': return 'bg-green-100';
    case 'meeting': return 'bg-purple-100';
    case 'linkedin': return 'bg-sky-100';
    default: return 'bg-gray-100';
  }
};

const getCallStatusDisplay = (status) => {
  if (!status) return null;
  const statusUpper = status.toUpperCase();
  const statusMap = {
    'ANSWER': 'Answered',
    'PICKED': 'Answered',
    'NOANSWER': 'Missed / No Answer',
    'BUSY': 'Busy',
    'CANCEL-AGENT': 'Cancelled by Agent',
    'CANCEL-CUSTOMER': 'Cancelled by Customer',
    'FAILED': 'Failed',
    'PENDING': 'Pending'
  };
  return statusMap[statusUpper] || status;
};

const getCallStatusColor = (status) => {
  if (!status) return 'bg-gray-100 text-gray-800';
  const statusUpper = status.toUpperCase();
  switch (statusUpper) {
    case 'ANSWER': return 'bg-green-100 text-green-800';
    case 'PICKED': return 'bg-green-100 text-green-800';
    case 'NOANSWER': return 'bg-yellow-100 text-yellow-800';
    case 'BUSY': return 'bg-orange-100 text-orange-800';
    case 'CANCEL-AGENT': return 'bg-red-100 text-red-800';
    case 'CANCEL-CUSTOMER': return 'bg-red-100 text-red-800';
    case 'FAILED': return 'bg-red-100 text-red-800';
    case 'PENDING': return 'bg-blue-100 text-blue-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const formatDuration = (minutes, callDurationSeconds = null) => {
  if (callDurationSeconds !== null && callDurationSeconds !== undefined) {
    const totalSeconds = Math.floor(callDurationSeconds);
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    if (hours > 0) {
      if (mins === 0 && secs === 0) return `${hours} hour${hours !== 1 ? 's' : ''}`;
      if (secs === 0) return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`;
      return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
    } else if (mins > 0) {
      if (secs === 0) return `${mins} minute${mins !== 1 ? 's' : ''}`;
      return `${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
    } else {
      return `${secs} second${secs !== 1 ? 's' : ''}`;
    }
  }

  if (!minutes) return '';
  const totalSeconds = Math.floor(minutes * 60);
  const hours = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  if (hours > 0) {
    if (mins === 0 && secs === 0) return `${hours} hour${hours !== 1 ? 's' : ''}`;
    if (secs === 0) return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`;
    return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
  } else if (mins > 0) {
    if (secs === 0) return `${mins} minute${mins !== 1 ? 's' : ''}`;
    return `${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
  } else {
    return `${secs} second${secs !== 1 ? 's' : ''}`;
  }
};

const getPerformerName = (performedBy) => {
  if (!performedBy) return 'Unknown User';
  if (typeof performedBy === 'string') return performedBy;
  if (performedBy.firstName && performedBy.lastName) {
    return `${performedBy.firstName} ${performedBy.lastName}`;
  } else if (performedBy.firstName) {
    return performedBy.firstName;
  } else if (performedBy.email) {
    return performedBy.email;
  }
  return 'Unknown User';
};

const LeadDetail360 = ({ onBack, onEdit, leadId: propLeadId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: paramId } = useParams();
  const id = propLeadId || paramId;
  const [searchParams, setSearchParams] = useSearchParams();

  // React Query Hooks
  const {
    data: leadData,
    isLoading: leadLoading,
    error: leadError
  } = useLead(id);

  const {
    data: activitiesData,
    isLoading: activitiesLoading,
    fetchNextPage: loadMoreActivities,
    hasNextPage: activitiesHasMore,
    isFetchingNextPage: activitiesLoadingMore
  } = useLeadActivities(id);

  const { data: masterSources = [] } = useMasterSources();
  const { data: leadStatuses = [] } = useLeadStatuses();
  const leadMutations = useLeadMutations();

  // Flatten infinite query activities
  const activities = useMemo(() => {
    return activitiesData?.pages.flatMap(page => page.activities) || [];
  }, [activitiesData]);

  // KPI Calculations
  const lastContactDate = useMemo(() => {
    // Priority 1: Manual last followup date
    if (leadData?.lastFollowupDate) return leadData.lastFollowupDate;

    // Priority 2: Latest contact activity from timeline
    const contactActivity = activities.find(a => ['call', 'email', 'meeting', 'whatsapp'].includes(a.type));
    if (contactActivity) return getActivityTime(contactActivity);

    // Priority 3: Manual last active date
    return leadData?.lastActiveDate || null;
  }, [activities, leadData?.lastFollowupDate, leadData?.lastActiveDate]);

  const nextFollowUpDate = useMemo(() => {
    // Priority 1: Manual next followup date
    if (leadData?.nextFollowupDate) return leadData.nextFollowupDate;

    // Priority 2: Next scheduled activity from timeline
    const futureActivities = activities
      .filter(a => a.scheduledFor && new Date(a.scheduledFor) > new Date())
      .sort((a, b) => new Date(a.scheduledFor) - new Date(b.scheduledFor));
    return futureActivities.length > 0 ? futureActivities[0].scheduledFor : null;
  }, [activities, leadData?.nextFollowupDate]);

  const latestFollowUpStatus = useMemo(() => {
    // Priority 1: Manual next action from lead data
    if (leadData?.nextAction) return leadData.nextAction;

    // Priority 2: Latest activity status from timeline
    if (activities.length > 0) {
      const latest = activities[0];
      return latest.outcome || latest.status || latest.type || 'N/A';
    }

    return 'N/A';
  }, [activities, leadData?.nextAction]);

  const lostReasons = useMemo(() => {
    if (Array.isArray(leadData?.lostReason)) {
      // Sort by date descending
      const sorted = [...leadData.lostReason].sort((a, b) => {
        const dateA = a && typeof a === 'object' ? new Date(a.date || 0) : new Date(0);
        const dateB = b && typeof b === 'object' ? new Date(b.date || 0) : new Date(0);
        return dateB - dateA;
      });

      return sorted.filter(lr => {
        if (!lr) return false;
        if (typeof lr === 'string') return true;
        if (lr.reason) return true;
        // Check for broken indexed objects
        return Object.keys(lr).some(k => !isNaN(parseInt(k)));
      });
    }
    return [];
  }, [leadData]);

  // Get initial tab from URL, default to 'profile'
  const initialTab = searchParams.get('tab') || 'profile';
  const [activeTab, setActiveTab] = useState(initialTab);

  const getDocumentDownloadFilename = (doc, legacyData = null) => {
    const pdfData = legacyData || doc?.companyPdf;
    if (!pdfData) return "document.pdf";

    // If companyPdf is the metadata object
    if (typeof pdfData === 'object' && pdfData !== null) {
      const fallbackName = pdfData.originalName || pdfData.name || doc?.documentTypeName || doc?.documentType || "document";
      if (/\.[a-zA-Z0-9]+$/.test(fallbackName)) return fallbackName;

      const mimeType = (pdfData.mimetype || pdfData.mimeType || "").toLowerCase();
      if (mimeType === "application/pdf") return `${fallbackName}.pdf`;
      return `${fallbackName}.pdf`;
    }

    // Fallback for string URLs
    const pdfUrl = String(pdfData || '');
    const parts = pdfUrl.split('/');
    const filenameFromUrl = parts[parts.length - 1] || "document.pdf";
    const baseName = doc?.documentTypeName || doc?.documentType || filenameFromUrl;

    if (/\.[a-zA-Z0-9]+$/.test(baseName)) return baseName;
    return `${baseName.toLowerCase().endsWith('.pdf') ? baseName : baseName + '.pdf'}`;
  };

  const handleDocumentDownload = async (doc, legacyData = null) => {
    const pdfData = legacyData || doc?.companyPdf;
    if (!pdfData) return;

    const url = typeof pdfData === 'object' && pdfData !== null ? pdfData.url : pdfData;
    const filename = getDocumentDownloadFilename(doc, legacyData);

    try {
      const downloadToast = toast.loading('Preparing download...');

      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch document");

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
      toast.dismiss(downloadToast);
    } catch (error) {

      // Fallback: Just open in new tab if blob download fails
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDownloadAttachment = async (e, att) => {
    e.preventDefault();
    e.stopPropagation();
    let downloadToast;
    try {
      const publicId = att.public_id || att.publicId;
      if (!publicId) {
        toast.error("File details not available for download");
        return;
      }
      
      downloadToast = toast.loading("Preparing download...");
      const fileName = att.fileName || att.filename || 'file';
      const token = localStorage.getItem('auth_token');
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const downloadUrl = `${apiBaseUrl}/upload/cloudinary/download?public_id=${encodeURIComponent(publicId)}&filename=${encodeURIComponent(fileName)}`;

      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);

      toast.success("Download complete!", { id: downloadToast });
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file.', { id: downloadToast });
    }
  };

  // Update URL when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    }, { replace: true });
  };

  // Sync activeTab with URL params on mount and when URL changes
  useEffect(() => {
    if (!isMountedRef.current) return;

    const tabFromUrl = searchParams.get('tab');
    const validTabs = ['profile', 'timeline', 'project_cost', 'documents', 'assignment_history', 'remarks'];

    if (tabFromUrl && validTabs.includes(tabFromUrl)) {
      // Only update if different to avoid unnecessary re-renders
      if (activeTab !== tabFromUrl) {
        setActiveTab(tabFromUrl);
      }
      tabInitializedRef.current = true;
    } else if (!tabFromUrl && !tabInitializedRef.current) {
      // If no tab in URL and not yet initialized, set default and update URL (only once)
      setActiveTab('profile');
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('tab', 'profile');
        return next;
      }, { replace: true });
      tabInitializedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Only depend on searchParams to avoid loops

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [notification, setNotification] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailHtml, setEmailHtml] = useState('');
  const [whatsAppMessage, setWhatsAppMessage] = useState('');
  const [meetingTitle, setMeetingTitle] = useState('');
  const [meetingNotes, setMeetingNotes] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingTime, setMeetingTime] = useState('');
  const [meetingDuration, setMeetingDuration] = useState('00:30');
  const [meetingLink, setMeetingLink] = useState('');
  const [meetingType, setMeetingType] = useState('video');
  const [meetingErrors, setMeetingErrors] = useState({});
  const [newRemark, setNewRemark] = useState('');
  const [isSubmittingRemark, setIsSubmittingRemark] = useState(false);

  // Use refs to track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  const notificationTimeoutRef = useRef(null);
  const tabInitializedRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);









  const showNotification = (message, type = 'success') => {
    if (!isMountedRef.current) return;

    // Clear any existing timeout
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }

    setNotification({ message, type });
    notificationTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setNotification(null);
      }
      notificationTimeoutRef.current = null;
    }, 3000);
  };

  // Show loading state
  if (leadLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F2942] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading lead details...</p>
        </div>
      </div>
    );
  }

  // Show error state if lead not found or error occurred
  if (leadError || !leadData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="p-3 bg-red-100 rounded-full mb-4 mx-auto w-fit">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Lead Not Found</h3>
          <p className="text-gray-600 mb-4">The lead you're looking for doesn't exist or has been deleted.</p>
          <div className="flex gap-3 justify-center">
            <Button
              variant="primary"
              onClick={() => onBack ? onBack() : navigate(location.state?.from || '/lead_list_filters')}
              icon={<ArrowLeft className="h-4 w-4" />}
            >
              Back to Lead List
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const getProjectCostNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const hasProjectCostNumber = (value) => {
    if (value === null || value === undefined || value === '') return false;
    return Number.isFinite(Number(value));
  };

  const projectCostType = leadData.costType === 'one_time' ? 'fixed' : (leadData.costType || '');
  const recurringCostSubtotal = projectCostType === 'recurring'
    ? (getProjectCostNumber(leadData.costQuarterly) || (getProjectCostNumber(leadData.costDurationMonths) * getProjectCostNumber(leadData.costMrr)))
    : 0;
  const projectTotalCost = projectCostType === 'fixed'
    ? getProjectCostNumber(leadData.totalCost)
    : projectCostType === 'recurring'
      ? (getProjectCostNumber(leadData.totalCost) || recurringCostSubtotal + getProjectCostNumber(leadData.costOneTime))
      : getProjectCostNumber(leadData.totalCost);
  const hasProjectCost = !!projectCostType || [
    leadData.costOneTime,
    leadData.costMrr,
    leadData.costDurationMonths,
    leadData.costQuarterly,
    leadData.totalCost
  ].some(value => getProjectCostNumber(value) > 0);

  const formatProjectAmount = (value, showZero = false) => {
    if (!hasProjectCostNumber(value)) return 'N/A';
    const amount = getProjectCostNumber(value);
    if (!showZero && amount === 0) return 'N/A';
    return `₹${formatIndianNumber(amount)}`;
  };

  const projectCostTypeLabel = projectCostType === 'fixed'
    ? 'Fixed'
    : projectCostType === 'recurring'
      ? 'Recurring'
      : 'N/A';

  const renderProjectCostLine = (label, value, options = {}) => (
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 py-3">
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <div className="sm:text-right">
        <p className="text-base font-semibold text-gray-900 break-words">{value}</p>
        {options.note && <p className="text-xs text-gray-500 mt-1">{options.note}</p>}
      </div>
    </div>
  );





  const handleLogActivity = () => {
    navigate(`/log_activity/${id}`);
  };

  const handleEditLead = () => {
    if (onEdit) {
      onEdit(id);
    } else {
      navigate(`/manual_lead_entry?edit=${id}`);
    }
  };

  const handleEmailLead = () => {
    if (leadData?.email) {
      setShowEmailModal(true);
    } else {
      showNotification('No email address available for this lead', 'error');
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim()) {
      showNotification('Please enter an email subject', 'error');
      return;
    }
    if (!emailHtml.trim()) {
      showNotification('Please enter an email message', 'error');
      return;
    }

    try {
      const result = await leadMutations.sendEmail.mutateAsync({
        leadId: id,
        emailData: {
          to: leadData?.email,
          subject: emailSubject.trim(),
          bodyHtml: emailHtml,
          bodyText: emailHtml.replace(/<[^>]*>/g, '') // Strip HTML for text version
        }
      });

      if (result.success) {
        showNotification('Email sent successfully!', 'success');
        setShowEmailModal(false);
        setEmailSubject('');
        setEmailHtml('');
        handleTabChange('timeline');
      } else {
        showNotification(result.error || 'Failed to send email', 'error');
      }
    } catch (error) {

      showNotification('Failed to send email. Please try again.', 'error');
    }
  };

  const handleWhatsAppLead = () => {
    if (leadData?.phone) {
      setShowWhatsAppModal(true);
    } else {
      showNotification('No phone number available for WhatsApp', 'error');
    }
  };

  const handleScheduleMeeting = () => {
    setShowScheduleModal(true);
    // Reset errors when opening modal
    setMeetingErrors({});
  };

  const validateMeetingForm = () => {
    const newErrors = {};

    // Meeting title validation
    if (!meetingTitle.trim()) {
      newErrors.meetingTitle = 'Meeting title is required.';
    } else if (meetingTitle.trim().length > 200) {
      newErrors.meetingTitle = 'Meeting title cannot exceed 200 characters.';
    }

    // Meeting date validation
    if (!meetingDate) {
      newErrors.meetingDate = 'Meeting date is required.';
    } else {
      const selectedDate = new Date(meetingDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        newErrors.meetingDate = 'Meeting date cannot be in the past.';
      } else if (selectedDate.getTime() === today.getTime()) {
        // If date is today, validate time is not in the past
        if (meetingTime) {
          const [hours, minutes] = meetingTime.split(':');
          const selectedDateTime = new Date();
          selectedDateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
          const now = new Date();

          if (selectedDateTime < now) {
            newErrors.meetingTime = 'Meeting time cannot be in the past.';
          }
        }
      }
    }

    // Meeting time validation
    if (!meetingTime) {
      newErrors.meetingTime = 'Meeting time is required.';
    }

    // Meeting link validation
    if (!meetingLink.trim()) {
      newErrors.meetingLink = 'Meeting link is required.';
    } else {
      // Normalize URL - add https:// if no protocol is present
      let normalizedLink = meetingLink.trim();
      if (!normalizedLink.match(/^https?:\/\//i)) {
        normalizedLink = `https://${normalizedLink}`;
      }

      // Validate URL format
      try {
        new URL(normalizedLink);
      } catch (e) {
        newErrors.meetingLink = 'Please enter a valid meeting link URL.';
      }
    }

    // Duration validation - convert time format (HH:MM) to minutes
    if (!meetingDuration || !meetingDuration.trim()) {
      newErrors.meetingDuration = 'Meeting duration is required.';
    } else {
      const timePattern = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
      if (!timePattern.test(meetingDuration.trim())) {
        newErrors.meetingDuration = 'Please enter a valid duration in HH:MM format (e.g., 00:30 for 30 minutes).';
      } else {
        const [hours, minutes] = meetingDuration.trim().split(':').map(Number);
        const totalMinutes = hours * 60 + minutes;
        if (totalMinutes <= 0) {
          newErrors.meetingDuration = 'Meeting duration must be greater than 0 minutes.';
        } else if (totalMinutes > 480) {
          newErrors.meetingDuration = 'Meeting duration cannot exceed 8 hours (480 minutes).';
        }
      }
    }

    // Notes validation (optional but check length if provided)
    if (meetingNotes.trim().length > FIELD_LIMITS.notes) {
      newErrors.meetingNotes = `Notes cannot exceed ${FIELD_LIMITS.notes} characters.`;
    }

    setMeetingErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirmScheduleMeeting = async () => {
    // Validate form
    if (!validateMeetingForm()) {
      return;
    }

    if (!leadData?.email) {
      showNotification('No email address available for this lead to send calendar invite', 'error');
      return;
    }

    try {
      const localDateTimeString = `${meetingDate}T${meetingTime}`;
      const scheduledStart = new Date(localDateTimeString);
      if (isNaN(scheduledStart.getTime())) {
        showNotification('Invalid date or time selected', 'error');
        return;
      }

      let normalizedMeetingLink = meetingLink.trim();
      if (!normalizedMeetingLink.match(/^https?:\/\//i)) {
        normalizedMeetingLink = `https://${normalizedMeetingLink}`;
      }

      const [hours, minutes] = meetingDuration.trim().split(':').map(Number);
      const durationMinutes = hours * 60 + minutes;

      const activityPayload = {
        type: 'meeting',
        title: meetingTitle.trim(),
        description: `${meetingNotes.trim() || 'Meeting scheduled via Quick Actions.'}`,
        duration: durationMinutes,
        scheduledFor: scheduledStart.toISOString(),
        meetingLink: normalizedMeetingLink,
        notes: meetingNotes.trim() || undefined,
        outcome: 'scheduled',
        priority: 'medium'
      };

      const result = await leadMutations.scheduleMeeting.mutateAsync({
        leadId: id,
        activityData: activityPayload
      });

      if (result.success) {
        showNotification('Meeting scheduled and invite sent successfully!', 'success');
        setShowScheduleModal(false);
        setMeetingTitle('');
        setMeetingNotes('');
        setMeetingDate('');
        setMeetingTime('');
        setMeetingDuration('00:30');
        setMeetingLink('');
        setMeetingType('video');
        setMeetingErrors({});
        handleTabChange('timeline');
      } else {
        showNotification(result.error || 'Failed to schedule meeting. Please try again.', 'error');
      }
    } catch (error) {

      showNotification('Failed to schedule meeting. Please try again.', 'error');
    }
  };

  const handleViewActivity = (activityId) => {
    if (!activityId) return;
    navigate(`/activity_detail/${activityId}`);
  };

  const handleCreateQuote = () => {
    // Navigate to quote builder with the current lead ID
    navigate(`/quote_builder?leadId=${id || leadData?.leadId || ''}`);
  };

  return (
    <div className="space-y-6">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg max-w-sm ${notification.type === 'success'
          ? 'bg-green-500 text-white'
          : notification.type === 'error'
            ? 'bg-red-500 text-white'
            : notification.type === 'info'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-500 text-white'
          }`}>
          <div className="flex items-center">
            <div className="flex-1">
              <div className="text-sm font-medium">{notification.message}</div>
            </div>
            <button
              onClick={() => setNotification(null)}
              className="ml-4 text-white hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col space-y-4">
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => onBack ? onBack() : navigate(location.state?.from || -1)}
            className="flex-shrink-0"
          >
            Back
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 break-words capitalize">{getLeadName(leadData)}</h1>
            <p className="text-sm sm:text-base text-gray-600 break-words first-letter:capitalize">{leadData.company || 'No company'}</p>
          </div>
          <PermissionGate permission="lead_management" action="update">
            <Button
              variant="primary"
              icon={<Edit className="h-4 w-4" />}
              onClick={handleEditLead}
              className="flex-shrink-0"
            >
              Edit Lead
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Lead Info Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Basic Info */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Basic Information</h3>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <User className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 break-words capitalize">{getLeadName(leadData)}</p>
                <p className="text-xs text-gray-500">Contact Person</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Building className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 break-words first-letter:capitalize">{leadData.company || 'No company'}</p>
                <p className="text-xs text-gray-500">Company</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 break-words">{leadData.email || 'No email'}</p>
                <p className="text-xs text-gray-500">Email</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Phone className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 break-words">{leadData.phone || 'No phone'}</p>
                <p className="text-xs text-gray-500">Phone</p>
              </div>
            </div>
            {leadData.alternatePhone && (
              <div className="flex items-center space-x-3">
                <Phone className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 break-words">{leadData.alternatePhone}</p>
                  <p className="text-xs text-gray-500">Alternate Phone</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Lead Status */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Lead Status</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm text-gray-500">Score:</span>
              <span className={`inline-flex items-center px-2 py-0.5 sm:px-2.5 rounded-full text-xs font-medium ${getScoreColor(leadData.scoreLabel || 'Cold')}`}>
                {leadData.score !== null && leadData.score !== undefined ? `${leadData.score}` : 'N/A'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm text-gray-500">Status:</span>
              <span className={`inline-flex items-center px-2 py-0.5 sm:px-2.5 rounded-full text-xs font-medium capitalize ${getStatusColor(leadData.stage || leadData.status || 'New', leadStatuses)}`}>
                {getStatusDisplay(leadData.stage || leadData.status || 'New', leadStatuses)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm text-gray-500">Owner:</span>
              <div className="text-right">
                <p className="text-xs sm:text-sm font-medium text-gray-900 break-words first-letter:capitalize">
                  {leadData.owner || (leadData.assignedTo ? getPerformerName(leadData.assignedTo) : 'Unassigned')}
                </p>
                {(leadData.ownerEmail || (leadData.assignedTo && typeof leadData.assignedTo === 'object' && leadData.assignedTo.email)) && (
                  <p className="text-xs text-gray-500 break-words">
                    {leadData.ownerEmail || leadData.assignedTo?.email}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm text-gray-500">Original Generator:</span>
              <div className="text-right">
                <p className="text-xs sm:text-sm font-medium text-gray-900 break-words first-letter:capitalize">
                  {leadData.originalGenerator
                    ? (typeof leadData.originalGenerator === 'object'
                      ? getPerformerName(leadData.originalGenerator)
                      : leadData.originalGenerator)
                    : 'N/A'}
                </p>
                {leadData.originalGenerator && typeof leadData.originalGenerator === 'object' && leadData.originalGenerator.email && (
                  <p className="text-xs text-gray-500 break-words">{leadData.originalGenerator.email}</p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm text-gray-500">Source:</span>
              <span className="text-xs sm:text-sm font-medium text-gray-900 break-words" title={mapSourceToDisplay(leadData.source, masterSources)}>{mapSourceToDisplay(leadData.source, masterSources)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm text-gray-500">Category:</span>
              <span className="text-xs sm:text-sm font-medium text-gray-900 break-words first-letter:capitalize">
                {leadData.category?.name || 'N/A'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm text-gray-500">Project Cost:</span>
              <span className="text-xs sm:text-sm font-medium text-gray-900">{formatProjectAmount(projectTotalCost, true)}</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2 sm:gap-3">
            <PermissionGate permission="lead_management" action="read">
              <Button
                variant="outline"
                size="sm"
                icon={<Mail className="h-3 w-3 sm:h-4 sm:w-4" />}
                onClick={handleEmailLead}
                className="w-full justify-start text-xs sm:text-sm"
              >
                Send Email
              </Button>
            </PermissionGate>
            <PermissionGate permission="lead_management" action="read">
              <Button
                variant="outline"
                size="sm"
                icon={<MessageSquare className="h-3 w-3 sm:h-4 sm:w-4" />}
                onClick={handleWhatsAppLead}
                className="w-full justify-start text-xs sm:text-sm"
              >
                WhatsApp
              </Button>
            </PermissionGate>
            <PermissionGate permission="lead_management" action="update">
              <Button
                variant="outline"
                size="sm"
                icon={<Calendar className="h-3 w-3 sm:h-4 sm:w-4" />}
                onClick={handleScheduleMeeting}
                className="w-full justify-start text-xs sm:text-sm"
              >
                Schedule Meeting
              </Button>
            </PermissionGate>
          </div>
        </div>
      </div>

      {/* Lost Reason Section */}
      {lostReasons.length > 0 && leadData?.stage === 'closed_lost' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 sm:p-6 shadow-sm">
          <div className="flex items-start space-x-3">
            <div className="bg-red-100 p-2 rounded-full flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-red-900">
                {lostReasons.length > 1 ? 'Loss Reasons' : 'Loss Reason'}
              </h3>
              <div className="mt-1 space-y-3">
                {lostReasons.map((lr, index) => {
                  let reasonText = typeof lr === 'string' ? lr : lr.reason;

                  // Fallback for broken indexed objects
                  if (!reasonText && typeof lr === 'object' && lr !== null) {
                    const indexedKeys = Object.keys(lr).filter(k => !isNaN(parseInt(k))).sort((a, b) => parseInt(a) - parseInt(b));
                    if (indexedKeys.length > 0) {
                      reasonText = indexedKeys.map(k => lr[k]).join('');
                    }
                  }

                  const reasonDate = typeof lr === 'object' ? lr.date : null;

                  return (
                    <div key={index} className="flex flex-col">
                      <p className="text-sm sm:text-base text-red-700 break-words flex items-start first-letter:capitalize">
                        <span className="mr-2 font-bold">•</span>
                        {reasonText}
                      </p>
                      {reasonDate && (
                        <span className="text-xs text-red-500 ml-5 mt-0.5">
                          {formatDate(reasonDate)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex overflow-x-auto px-4 sm:px-6">
            <div className="flex space-x-4 sm:space-x-8 min-w-max">
              {[
                { key: 'profile', label: 'Profile' },
                { key: 'timeline', label: 'Timeline' },
                { key: 'project_cost', label: 'Project Cost' },
                { key: 'documents', label: 'Documents' },
                { key: 'assignment_history', label: 'Assignment History' },
                { key: 'remarks', label: 'Remarks' }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${activeTab === tab.key
                    ? 'border-agrosaf-green text-agrosaf-green'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </nav>
        </div>

        <div className="p-4 sm:p-6">
          {activeTab === 'timeline' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Activity Timeline</h3>
              </div>

              {/* KPI Metrics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Follow-up Status</span>
                    <Clock className="h-4 w-4 text-blue-500" />
                  </div>
                  <div className="text-sm font-semibold text-gray-900 capitalize">
                    {latestFollowUpStatus || 'N/A'}
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Next Follow-up</span>
                    <Calendar className="h-4 w-4 text-purple-500" />
                  </div>
                  <div className="text-sm font-semibold text-gray-900">
                    {nextFollowUpDate ? formatDate(nextFollowUpDate, { includeTime: true }) : 'N/A'}
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Last Contact</span>
                    <Mail className="h-4 w-4 text-green-500" />
                  </div>
                  <div className="text-sm font-semibold text-gray-900">
                    {lastContactDate ? stringUtils.formatDate(lastContactDate, { includeSeconds: true }) : 'N/A'}
                  </div>
                </div>
              </div>

              {activitiesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading activities...</p>
                  </div>
                </div>
              ) : activities.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No activities logged yet</p>
                  <p className="text-sm text-gray-500 mt-2">Activities will appear here once logged</p>
                  <PermissionGate permission="lead_management" action="create">
                    <Button
                      variant="primary"
                      icon={<Plus className="h-4 w-4" />}
                      onClick={handleLogActivity}
                      className="mt-4 bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                    >
                      Log First Activity
                    </Button>
                  </PermissionGate>
                </div>
              ) : (
                <div className="relative">
                  {/* Timeline */}
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                  <div className="space-y-6">
                    {activities.map((activity, index) => {
                      const ActivityIcon = getActivityIcon(activity.type);
                      const iconColor = getActivityColor(activity.type);
                      const bgColor = getActivityBgColor(activity.type);

                      return (
                        <div
                          key={activity._id || activity.activityId}
                          className="relative pl-12 cursor-pointer"
                          onClick={() => handleViewActivity(activity._id || activity.activityId)}
                        >
                          {/* Timeline dot */}
                          <div className={`absolute left-0 top-1 w-8 h-8 rounded-full ${bgColor} flex items-center justify-center border-2 border-white shadow-sm`}>
                            <ActivityIcon className={`h-4 w-4 ${iconColor}`} />
                          </div>

                          {/* Activity card */}
                          <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className={`text-base sm:text-lg font-semibold text-gray-900 first-letter:capitalize ${activity.type === 'email' ? 'truncate' : ''
                                    }`}>
                                    {activity.title}
                                  </h4>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${activity.type === 'call' ? 'bg-blue-100 text-blue-800' :
                                    activity.type === 'email' ? 'bg-green-100 text-green-800' :
                                      activity.type === 'whatsapp' ? 'bg-green-100 text-green-800' :
                                        activity.type === 'meeting' ? 'bg-purple-100 text-purple-800' :
                                          'bg-gray-100 text-gray-800'
                                    }`}>
                                    {activity.type}
                                  </span>
                                  {activity.isAutomated && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                      Auto
                                    </span>
                                  )}
                                  {activity.type === 'call' && activity.callMetadata?.status && (
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getCallStatusColor(activity.callMetadata.status)}`}>
                                      {getCallStatusDisplay(activity.callMetadata.status)}
                                    </span>
                                  )}
                                </div>

                                {activity.description && (
                                  <p className={`text-sm text-gray-600 mb-3 break-words ${activity.type === 'email' ? 'line-clamp-2' : 'whitespace-pre-wrap'
                                    }`}>
                                    {activity.description}
                                  </p>
                                )}

                                {activity.attachments && activity.attachments.length > 0 && (
                                  <div className="flex flex-wrap gap-2 mb-3">
                                    {activity.attachments.map((att, attIdx) => (
                                      <button
                                        key={attIdx}
                                        type="button"
                                        onClick={(e) => handleDownloadAttachment(e, att)}
                                        className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-100 hover:border-gray-300 transition-colors focus:outline-none text-left"
                                      >
                                        <Paperclip className="h-3.5 w-3.5 text-gray-400" />
                                        <span className="truncate max-w-[150px]">{att.fileName}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}

                                <div className="flex flex-wrap items-center gap-4 text-xs sm:text-sm text-gray-500">
                                  <div className="flex items-center gap-1">
                                    <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                                    <span>{formatDate(getActivityTime(activity))}</span>
                                  </div>

                                  {(() => {
                                    // Show duration for calls (talkDuration) or meetings (duration in minutes)
                                    if (activity.type === 'call') {
                                      // ONLY show actual talk time (talkDuration) - no fallbacks
                                      const talkDuration = activity.callMetadata?.talkDuration;
                                      return talkDuration !== null && talkDuration !== undefined && talkDuration > 0 ? (
                                        <div className="flex items-center gap-1">
                                          <span>Duration: {formatDuration(null, talkDuration)}</span>
                                        </div>
                                      ) : null;
                                    } else if (activity.type === 'meeting' && activity.duration) {
                                      // Show meeting duration
                                      return (
                                        <div className="flex items-center gap-1">
                                          <span>Duration: {formatDuration(activity.duration)}</span>
                                        </div>
                                      );
                                    }
                                    return null;
                                  })()}

                                  {activity.performedBy && (
                                    <div className="flex items-center gap-1">
                                      <User className="h-3 w-3 sm:h-4 sm:w-4" />
                                      <span>{getPerformerName(activity.performedBy)}</span>
                                    </div>
                                  )}

                                  {activity.outcome && (
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${activity.outcome === 'completed' ? 'bg-green-100 text-green-800' :
                                      activity.outcome === 'sent' ? 'bg-green-100 text-green-800' :
                                        activity.outcome === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                                          activity.outcome === 'cancelled' ? 'bg-red-100 text-red-800' :
                                            'bg-gray-100 text-gray-800'
                                      }`}>
                                      {activity.outcome}
                                    </span>
                                  )}
                                </div>

                                {activity.scheduledFor && (
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="flex items-center gap-2 text-sm text-[#0F2942] mb-2">
                                      <Calendar className="h-4 w-4" />
                                      <span>Scheduled for: {formatDate(activity.scheduledFor)}</span>
                                    </div>
                                    {activity.meetingLink && (
                                      <div className="mt-2">
                                        {(() => {
                                          // Check if meeting is in the past (more than 10 minutes after scheduled time)
                                          const scheduledTime = new Date(activity.scheduledFor);
                                          const meetingEndTime = new Date(scheduledTime.getTime() + (activity.duration || 30) * 60 * 1000);
                                          const now = new Date();
                                          const tenMinutesAfterEnd = new Date(meetingEndTime.getTime() + 10 * 60 * 1000);
                                          const isPastMeeting = now > tenMinutesAfterEnd;

                                          if (isPastMeeting) {
                                            return (
                                              <button
                                                disabled
                                                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-400 text-white rounded-lg cursor-not-allowed text-sm font-medium opacity-60"
                                                title="This meeting has ended"
                                              >
                                                <Calendar className="h-4 w-4" />
                                                Join Meeting (Expired)
                                              </button>
                                            );
                                          }

                                          return (
                                            <a
                                              href={activity.meetingLink}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                                            >
                                              <Calendar className="h-4 w-4" />
                                              Join Meeting
                                            </a>
                                          );
                                        })()}
                                      </div>
                                    )}
                                  </div>
                                )}

                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {activitiesHasMore && (
                      <div className="flex justify-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={loadMoreActivities}
                          disabled={activitiesLoadingMore}
                        >
                          {activitiesLoadingMore ? 'Loading...' : 'View more'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Complete Lead Profile</h3>

              {/* Personal Information */}
              <div className="border-b border-gray-200 pb-4">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Personal Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">First Name</label>
                    <p className="text-sm text-gray-900 mt-1 break-words capitalize">{leadData.firstName || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Last Name</label>
                    <p className="text-sm text-gray-900 mt-1 break-words capitalize">{leadData.lastName || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <p className="text-sm text-gray-900 mt-1 break-words">{leadData.email || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Phone</label>
                    <p className="text-sm text-gray-900 mt-1 break-words">{leadData.phone || 'N/A'}</p>
                  </div>
                  {leadData.alternatePhone && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Alternate Phone</label>
                      <p className="text-sm text-gray-900 mt-1 break-words">{leadData.alternatePhone}</p>
                    </div>
                  )}
                  {leadData.whatsappPhone && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">WhatsApp Phone</label>
                      <p className="text-sm text-gray-900 mt-1 break-words">{leadData.whatsappPhone}</p>
                    </div>
                  )}
                  {leadData.designation && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Designation</label>
                      <p className="text-sm text-gray-900 mt-1 break-words capitalize">{leadData.designation}</p>
                    </div>
                  )}
                  {leadData.linkedinProfileUrl && (
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-gray-500">LinkedIn Profile</label>
                      <p className="text-sm text-gray-900 mt-1 break-words">
                        <a href={leadData.linkedinProfileUrl.startsWith('http') ? leadData.linkedinProfileUrl : `https://${leadData.linkedinProfileUrl}`}
                          target="_blank" rel="noopener noreferrer" className="text-[#0F2942] hover:underline">
                          {leadData.linkedinProfileUrl}
                        </a>
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium text-gray-500">Decision Maker</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">
                      {leadData.decisionMaker?.name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Lead Temperature</label>
                    <div className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${leadData.scoreLabel === 'hot' ? 'bg-red-100 text-red-800' :
                        leadData.scoreLabel === 'warm' ? 'bg-orange-100 text-orange-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                        {leadData.scoreLabel === 'hot' ? '🔥 Hot' :
                          leadData.scoreLabel === 'warm' ? '☀️ Warm' :
                            '❄️ Cold'
                        }
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Original Generator</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">
                      {leadData.originalGenerator
                        ? (typeof leadData.originalGenerator === 'object'
                          ? getPerformerName(leadData.originalGenerator)
                          : leadData.originalGenerator)
                        : 'N/A'}
                    </p>
                    {leadData.originalGenerator && typeof leadData.originalGenerator === 'object' && leadData.originalGenerator.email && (
                      <p className="text-xs text-gray-500 mt-0.5 break-words">{leadData.originalGenerator.email}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Business Information */}
              <div className="border-b border-gray-200 pb-4">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Business Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Company Name</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">{leadData.company || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Website</label>
                    <p className="text-sm text-gray-900 mt-1 break-words">
                      {leadData.website ? (
                        <a href={leadData.website.startsWith('http') ? leadData.website : `https://${leadData.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#0F2942] hover:underline break-words">
                          {leadData.website}
                        </a>
                      ) : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Industry</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">
                      {leadData.industry?.name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Company Size</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">
                      {leadData.companysize?.name || leadData.companySize || leadData.employeeCount || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Annual Revenue</label>
                    <p className="text-sm text-gray-900 mt-1 break-words">
                      {leadData.annualRevenue ? `₹${formatIndianNumber(leadData.annualRevenue)}` : 'N/A'}
                    </p>
                  </div>
                  {leadData.gstNumber && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">GST Number</label>
                      <p className="text-sm text-gray-900 mt-1 break-words uppercase">{leadData.gstNumber}</p>
                    </div>
                  )}
                  {leadData.drugLicenseNumber && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Drug License</label>
                      <p className="text-sm text-gray-900 mt-1 break-words uppercase">{leadData.drugLicenseNumber}</p>
                    </div>
                  )}

                </div>
              </div>

              {/* Address Information */}
              <div className="border-b border-gray-200 pb-4">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Address Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-gray-500">Street Address</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">
                      {leadData.locations?.[0]?.address?.street ||
                        (typeof leadData.address === 'string' ? leadData.address : leadData.address?.street) ||
                        'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">City</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">
                      {leadData.locations?.[0]?.address?.city?.name ||
                        leadData.city ||
                        leadData.address?.city ||
                        'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">State</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">
                      {leadData.locations?.[0]?.address?.state?.name ||
                        leadData.state ||
                        leadData.address?.state ||
                        'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Postal Code</label>
                    <p className="text-sm text-gray-900 mt-1 break-words">
                      {leadData.locations?.[0]?.address?.postalCode ||
                        leadData.pincode ||
                        leadData.address?.pincode ||
                        'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Country</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">
                      {leadData.locations?.[0]?.country?.name ||
                        leadData.country ||
                        leadData.address?.country ||
                        'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Region</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">{leadData.region || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Lead Information */}
              <div className="border-b border-gray-200 pb-4">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Lead Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Source</label>
                    <p className="text-sm text-gray-900 mt-1 break-words">{mapSourceToDisplay(leadData.source, masterSources)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Lead Category</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">
                      {leadData.category?.name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Expected Order Value</label>
                    <p className="text-sm text-gray-900 mt-1 break-words">
                      {leadData.budget ? `₹${formatIndianNumber(leadData.budget)}` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Project Cost</label>
                    <p className="text-sm text-gray-900 mt-1 break-words">
                      {formatProjectAmount(projectTotalCost, true)}
                    </p>
                  </div>
                  {leadData.revenueMonthly && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Revenue Monthly</label>
                      <p className="text-sm text-gray-900 mt-1 break-words">₹{formatIndianNumber(leadData.revenueMonthly)}</p>
                    </div>
                  )}
                  {leadData.revenueOneTime && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Revenue One Time</label>
                      <p className="text-sm text-gray-900 mt-1 break-words">₹{formatIndianNumber(leadData.revenueOneTime)}</p>
                    </div>
                  )}
                  {leadData.revenueType && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Revenue Type</label>
                      <p className="text-sm text-gray-900 mt-1 break-words">{leadData.revenueType}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-500">Timeline</label>
                    <p className="text-sm text-gray-900 mt-1 break-words first-letter:capitalize">{leadData.timeline || 'N/A'}</p>
                  </div>
                  {leadData.productRequirement && (
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-gray-500">Product/Requirement</label>
                      <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap break-words">{leadData.productRequirement}</p>
                    </div>
                  )}
                  {leadData.leadGenerationDate && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Lead Generation Date</label>
                      <p className="text-sm text-gray-900 mt-1 break-words">{formatDate(leadData.leadGenerationDate).split(',')[0]}</p>
                    </div>
                  )}
                  {leadData.leadBucket && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Lead Bucket</label>
                      <p className="text-sm text-gray-900 mt-1 break-words">{leadData.leadBucket}</p>
                    </div>
                  )}
                  {leadData.businessUnit && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Business Unit</label>
                      <p className="text-sm text-gray-900 mt-1 break-words">{leadData.businessUnit}</p>
                    </div>
                  )}
                  {leadData.probability && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Probability</label>
                      <p className="text-sm text-gray-900 mt-1 break-words">{leadData.probability}%</p>
                    </div>
                  )}
                  {leadData.expectedCloseDate && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Expected Close Date</label>
                      <p className="text-sm text-gray-900 mt-1 break-words">{formatDate(leadData.expectedCloseDate).split(',')[0]}</p>
                    </div>
                  )}
                  {leadData.stagingInstance && (
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-gray-500">Staging Instance</label>
                      <p className="text-sm text-gray-900 mt-1 break-words">
                        <a href={leadData.stagingInstance.startsWith('http') ? leadData.stagingInstance : `https://${leadData.stagingInstance}`}
                          target="_blank" rel="noopener noreferrer" className="text-[#0F2942] hover:underline">
                          {leadData.stagingInstance}
                        </a>
                      </p>
                    </div>
                  )}
                  {leadData.needManagementIntervention && (
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-gray-500">Management Intervention</label>
                      <p className="text-sm text-gray-900 mt-1 break-words capitalize">{leadData.needManagementIntervention}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Information */}
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-4">Additional Information</h4>
                <div className="space-y-4">
                  {leadData.contactDetailsRaw && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Contact Details Raw</label>
                      <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap break-words">{leadData.contactDetailsRaw}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-500">Description</label>
                    <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap break-words first-letter:capitalize">{leadData.notes || leadData.description || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Remarks</label>
                    <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap break-words first-letter:capitalize">{leadData.internalNotes || 'N/A'}</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {leadData.nextAction && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Next Action</label>
                        <p className="text-sm text-gray-900 mt-1 break-words">{leadData.nextAction}</p>
                      </div>
                    )}
                    {leadData.lastActiveChannel && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Last Active Channel</label>
                        <p className="text-sm text-gray-900 mt-1 break-words">{leadData.lastActiveChannel}</p>
                      </div>
                    )}
                    {leadData.lastActiveDate && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Last Active Date</label>
                        <p className="text-sm text-gray-900 mt-1 break-words">{formatDate(leadData.lastActiveDate).split(',')[0]}</p>
                      </div>
                    )}
                    {leadData.lastFollowupDate && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Last Followup Date</label>
                        <p className="text-sm text-gray-900 mt-1 break-words">{formatDate(leadData.lastFollowupDate).split(',')[0]}</p>
                      </div>
                    )}
                    {leadData.nextFollowupDate && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Next Follow-up Date</label>
                        <p className="text-sm text-gray-900 mt-1 break-words">{formatDate(leadData.nextFollowupDate).split(',')[0]}</p>
                      </div>
                    )}
                    {leadData.followupCadence && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Follow-up Cadence</label>
                        <p className="text-sm text-gray-900 mt-1 break-words">{leadData.followupCadence}</p>
                      </div>
                    )}
                    {leadData.followupCount !== undefined && leadData.followupCount !== null && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Followup Count</label>
                        <p className="text-sm text-gray-900 mt-1 break-words">{leadData.followupCount}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Metadata */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Metadata</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Hide Created By for auto-generated LinkedIn leads */}
                  {leadData.source !== 'linkedin' && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Created By</label>
                      <p className="text-sm text-gray-900 mt-1 break-words">
                        {leadData.createdBy
                          ? (typeof leadData.createdBy === 'object'
                            ? `${leadData.createdBy.firstName || ''} ${leadData.createdBy.lastName || ''}`.trim() || leadData.createdBy.email || 'N/A'
                            : leadData.createdBy)
                          : 'N/A'}
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-500">Created At</label>
                    <p className="text-sm text-gray-900 mt-1 break-words">{formatDate(leadData.createdAt)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Last Updated</label>
                    <p className="text-sm text-gray-900 mt-1 break-words">{formatDate(leadData.updatedAt || leadData.createdAt)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Last Activity</label>
                    <p className="text-sm text-gray-900 mt-1 break-words">{leadData.lastActivity || 'No activity'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'project_cost' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h3 className="text-lg font-semibold text-gray-900">Project Cost</h3>
                <span className="inline-flex w-fit items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                  {projectCostTypeLabel}
                </span>
              </div>

              {hasProjectCost ? (
                <>
                  <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-3xl">
                    {projectCostType === 'recurring' && (
                      <div className="divide-y divide-gray-100">
                        {renderProjectCostLine('One-Time Cost', formatProjectAmount(leadData.costOneTime, true))}
                        {renderProjectCostLine('Monthly Recurring Revenue', `${formatProjectAmount(leadData.costMrr, true)} / Month`)}
                        {renderProjectCostLine(
                          'Contract Duration',
                          hasProjectCostNumber(leadData.costDurationMonths) ? `${getProjectCostNumber(leadData.costDurationMonths)} Month${getProjectCostNumber(leadData.costDurationMonths) === 1 ? '' : 's'}` : 'N/A'
                        )}
                        {renderProjectCostLine('Total Recurring Value', formatProjectAmount(recurringCostSubtotal, true))}
                      </div>
                    )}

                    {projectCostType === 'fixed' && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <p className="text-sm font-medium text-gray-500">Total Cost</p>
                        <p className="text-2xl font-bold text-gray-900 mt-2">{formatProjectAmount(projectTotalCost, true)}</p>
                        <p className="text-xs text-gray-500 mt-2">A single agreed project cost.</p>
                      </div>
                    )}

                    {projectCostType === 'recurring' && (
                      <>
                        <div className="my-5 border-t border-gray-200" />
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-gray-500">Total Cost</p>
                            <p className="text-sm text-gray-600 mt-1">
                              {formatProjectAmount(leadData.costOneTime, true)} + {formatProjectAmount(recurringCostSubtotal, true)}
                            </p>
                          </div>
                          <p className="text-2xl font-bold text-gray-900">{formatProjectAmount(projectTotalCost, true)}</p>
                        </div>
                      </>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                  <Target className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 font-medium">No project cost has been added for this lead.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Lead Documents</h3>
                <span className="text-sm text-gray-500">
                  {Math.max((leadData.companyDocuments?.length || 0), (leadData.companyPdf ? 1 : 0))} Document(s)
                </span>
              </div>

              {((leadData.companyDocuments && leadData.companyDocuments.length > 0) || leadData.companyPdf) ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Handle new array format */}
                  {leadData.companyDocuments?.map((doc, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                      <div className="p-4 flex items-start justify-between bg-white border-b border-gray-200">
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-lg ${doc.documentMethod === 'upload' ? 'bg-blue-100 text-[#0F2942]' : 'bg-purple-100 text-purple-600'}`}>
                            {doc.documentMethod === 'upload' ? <FileText className="h-5 w-5" /> : <LinkIcon className="h-5 w-5" />}
                          </div>
                          <div>
                            <h4 className="text-sm font-bold text-gray-900 capitalize">
                              {doc.documentTypeName || doc.documentType || 'Unnamed Document'}
                            </h4>
                            <p className="text-xs text-gray-500 uppercase tracking-wider">{doc.documentMethod || 'upload'}</p>
                          </div>
                        </div>
                        {((doc.documentMethod === 'upload' && doc.companyPdf) || (doc.documentMethod === 'link' && doc.documentLink)) && (
                          doc.documentMethod === 'upload' ? (
                            <button
                              type="button"
                              onClick={() => handleDocumentDownload(doc)}
                              className="p-2 text-agrosaf-green hover:bg-green-50 rounded-full transition-colors"
                              title="Download Document"
                            >
                              <LinkIcon className="h-5 w-5" />
                            </button>
                          ) : (
                            <a
                              href={doc.documentLink?.startsWith('http') ? doc.documentLink : `https://${doc.documentLink}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-agrosaf-green hover:bg-green-50 rounded-full transition-colors"
                              title="Open Document"
                            >
                              <LinkIcon className="h-5 w-5" />
                            </a>
                          )
                        )}
                      </div>
                      <div className="p-4 grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs font-medium text-gray-500">Certificate Number</p>
                          <p className="text-sm text-gray-900 font-semibold mt-1">{doc.certificateNumber || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">Last Verified</p>
                          <p className="text-sm text-gray-900 mt-1">{formatDate(leadData.updatedAt)}</p>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Handle legacy companyPdf field if not in array */}
                  {(!leadData.companyDocuments || leadData.companyDocuments.length === 0) && leadData.companyPdf && (
                    <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                      <div className="p-4 flex items-start justify-between bg-white border-b border-gray-200">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 rounded-lg bg-blue-100 text-[#0F2942]">
                            <FileText className="h-5 w-5" />
                          </div>
                          <div>
                            <h4 className="text-sm font-bold text-gray-900">Legacy Attachment</h4>
                            <p className="text-xs text-gray-500 uppercase tracking-wider">PDF Upload</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDocumentDownload(null, leadData.companyPdf)}
                          className="p-2 text-agrosaf-green hover:bg-green-50 rounded-full transition-colors"
                          title="Download PDF"
                        >
                          <LinkIcon className="h-5 w-5" />
                        </button>
                      </div>
                      <div className="p-4">
                        <p className="text-xs font-medium text-gray-500">Note</p>
                        <p className="text-sm text-gray-600 mt-1 italic">This is a document from an older version of the lead system.</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                  <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 font-medium">No documents have been uploaded for this lead.</p>
                  <p className="text-sm text-gray-400 mt-1">Uploaded certifications and documents will appear here.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'assignment_history' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Assignment History</h3>
              {!leadData.assignmentHistory || leadData.assignmentHistory.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No assignment history found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned By</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {[...leadData.assignmentHistory].reverse().map((history, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col">
                              <span className="text-sm font-bold text-gray-900">{getPerformerName(history.assignedTo)}</span>
                              {history.assignedTo && typeof history.assignedTo === 'object' && (
                                <span className="text-[10px] text-gray-500 font-medium">
                                  {[
                                    history.assignedTo.employeeId ? `Emp ID: ${history.assignedTo.employeeId}` : null,
                                    history.assignedTo.role ? stringUtils.formatFieldName(history.assignedTo.role) : null,
                                    history.assignedTo.department
                                  ].filter(Boolean).join(' • ')}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col">
                              <span className="text-sm text-gray-900">{getPerformerName(history.assignedBy)}</span>
                              {history.assignedBy && typeof history.assignedBy === 'object' && (
                                <span className="text-[10px] text-gray-500">
                                  {[
                                    history.assignedBy.employeeId ? `Emp ID: ${history.assignedBy.employeeId}` : null,
                                    history.assignedBy.role ? stringUtils.formatFieldName(history.assignedBy.role) : null,
                                    history.assignedBy.department
                                  ].filter(Boolean).join(' • ')}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(history.assignedAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'remarks' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Remarks & Comments</h3>

              {/* Add Remark Form */}
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">Add a new remark</label>
                <div className="flex flex-col space-y-3">
                  <textarea
                    value={newRemark}
                    onChange={(e) => setNewRemark(e.target.value.slice(0, 3000))}
                    placeholder="Write your remark here..."
                    maxLength={3000}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-agrosaf-green focus:border-transparent min-h-[100px] text-sm"
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      {getCharacterCount(newRemark, 3000)}
                    </span>
                    <div className="flex justify-end">
                      <Button
                        variant="primary"
                        onClick={async () => {
                          if (!newRemark.trim()) {
                            toast.error('Remark cannot be empty');
                            return;
                          }
                          setIsSubmittingRemark(true);
                          try {
                            const result = await leadMutations.addRemark.mutateAsync({
                              leadId: id,
                              remark: newRemark.trim()
                            });
                            if (result.success || result._id) {
                              toast.success('Remark added successfully');
                              setNewRemark('');
                            } else {
                              toast.error('Failed to add remark');
                            }
                          } catch (err) {
                            toast.error(err.message || 'Failed to add remark');
                          } finally {
                            setIsSubmittingRemark(false);
                          }
                        }}
                        loading={isSubmittingRemark}
                        disabled={!newRemark.trim() || isSubmittingRemark}
                      >
                        Post Remark
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Remarks List */}
              <div className="space-y-4">
                {!leadData.remarks || leadData.remarks.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">No remarks yet. Be the first to add one!</p>
                  </div>
                ) : (
                  [...leadData.remarks].reverse().map((remark, index) => (
                    <div key={remark._id || index} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-start gap-3 mb-2">
                        <div className="h-8 w-8 rounded-full bg-agrosaf-green/10 flex items-center justify-center flex-shrink-0">
                          <User className="h-4 w-4 text-agrosaf-green" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div className="flex flex-col">
                              <span className="text-sm font-bold text-gray-900">{getPerformerName(remark.user)}</span>
                              {remark.user && typeof remark.user === 'object' && (
                                <span className="text-[10px] text-gray-500 font-medium">
                                  {[
                                    remark.user.employeeId ? `Emp ID: ${remark.user.employeeId}` : null,
                                    remark.user.role ? stringUtils.formatFieldName(remark.user.role) : null,
                                    remark.user.department
                                  ].filter(Boolean).join(' • ')}
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-gray-400 flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDate(remark.createdAt)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap first-letter:capitalize">{remark.remark}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>


      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Send Email</h3>
              <button
                onClick={() => setShowEmailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  To
                </label>
                <input
                  type="email"
                  value={leadData?.email || ''}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => {
                    const sanitized = sanitizeTextInput(e.target.value);
                    setEmailSubject(sanitized.slice(0, 200));
                  }}
                  placeholder="Enter email subject"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                  maxLength={200}
                />
                <div className="flex justify-end mt-1">
                  <span className="text-xs text-gray-500">
                    {getCharacterCount(emailSubject, 200)}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden">
                  <ReactQuill
                    theme="snow"
                    value={emailHtml}
                    onChange={setEmailHtml}
                    placeholder="Enter your message here..."
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                        [{ 'color': [] }, { 'background': [] }],
                        ['link'],
                        ['clean']
                      ]
                    }}
                    style={{
                      minHeight: '200px',
                      backgroundColor: 'white'
                    }}
                  />
                  <style>{`
                    .ql-editor {
                      min-height: 200px;
                      padding-top: 12px !important;
                    }
                    .ql-editor.ql-blank::before {
                      top: 12px !important;
                      left: 15px !important;
                      font-style: normal;
                      color: #9ca3af;
                    }
                  `}</style>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowEmailModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                icon={<Send className="h-4 w-4" />}
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                onClick={handleSendEmail}
                disabled={leadMutations.sendEmail.isPending}
              >
                {leadMutations.sendEmail.isPending ? 'Sending...' : 'Send Email'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Modal */}
      {showWhatsAppModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Send WhatsApp Message</h3>
              <button
                onClick={() => setShowWhatsAppModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={leadData?.phone || ''}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  rows={6}
                  value={whatsAppMessage}
                  onChange={(e) => {
                    const sanitized = sanitizeTextInput(e.target.value);
                    setWhatsAppMessage(sanitized.slice(0, FIELD_LIMITS.message));
                  }}
                  placeholder="Enter your WhatsApp message here..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                  maxLength={FIELD_LIMITS.message}
                />
                <div className="flex justify-end mt-1">
                  <span className="text-xs text-gray-500">
                    {getCharacterCount(whatsAppMessage, FIELD_LIMITS.message)}
                  </span>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <MessageSquare className="h-5 w-5 text-green-600" />
                  <span className="text-sm text-green-800">
                    This will open WhatsApp with your message pre-filled
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowWhatsAppModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                icon={<MessageSquare className="h-4 w-4" />}
                className="bg-green-600 hover:bg-green-700"
                onClick={() => {
                  const message = whatsAppMessage.trim() || "Hello! I'm reaching out regarding your inquiry.";
                  window.open(`https://wa.me/${leadData?.phone?.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`, '_blank');
                  showNotification('Opening WhatsApp...', 'success');
                  setShowWhatsAppModal(false);
                  setWhatsAppMessage('');
                }}
              >
                Open WhatsApp
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Meeting Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Schedule Meeting</h3>
              <button
                onClick={() => {
                  setShowScheduleModal(false);
                  // Reset errors when closing modal
                  setMeetingErrors({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meeting Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={meetingTitle}
                  onChange={(e) => {
                    const sanitized = sanitizeTextInput(e.target.value);
                    setMeetingTitle(sanitized.slice(0, 200));
                    // Clear error when user types
                    if (meetingErrors.meetingTitle) {
                      setMeetingErrors(prev => ({ ...prev, meetingTitle: undefined }));
                    }
                  }}
                  onBlur={() => {
                    // Validate on blur
                    if (!meetingTitle.trim()) {
                      setMeetingErrors(prev => ({ ...prev, meetingTitle: 'Meeting title is required.' }));
                    } else if (meetingTitle.trim().length > 200) {
                      setMeetingErrors(prev => ({ ...prev, meetingTitle: 'Meeting title cannot exceed 200 characters.' }));
                    }
                  }}
                  placeholder="Enter meeting title"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${meetingErrors.meetingTitle ? 'border-red-300' : 'border-gray-300'
                    }`}
                  maxLength={200}
                />
                <div className="flex justify-between items-center mt-1">
                  {meetingErrors.meetingTitle && (
                    <p className="text-sm text-red-600">{meetingErrors.meetingTitle}</p>
                  )}
                  <span className="text-xs text-gray-500 ml-auto">
                    {getCharacterCount(meetingTitle, 200)}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={meetingDate}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      const today = new Date().toISOString().split('T')[0];

                      // Prevent selecting past dates
                      if (selectedDate < today) {
                        setMeetingErrors(prev => ({ ...prev, meetingDate: 'Meeting date cannot be in the past.' }));
                        return;
                      }

                      setMeetingDate(selectedDate);
                      // Clear error when user selects valid date
                      if (meetingErrors.meetingDate) {
                        setMeetingErrors(prev => ({ ...prev, meetingDate: undefined }));
                      }
                    }}
                    onBlur={() => {
                      // Validate on blur
                      if (!meetingDate) {
                        setMeetingErrors(prev => ({ ...prev, meetingDate: 'Meeting date is required.' }));
                      } else {
                        const selectedDate = new Date(meetingDate);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (selectedDate < today) {
                          setMeetingErrors(prev => ({ ...prev, meetingDate: 'Meeting date cannot be in the past.' }));
                        }
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${meetingErrors.meetingDate ? 'border-red-300' : 'border-gray-300'
                      }`}
                  />
                  {meetingErrors.meetingDate && (
                    <p className="text-sm text-red-600 mt-1">{meetingErrors.meetingDate}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={meetingTime}
                    onChange={(e) => {
                      const selectedTime = e.target.value;

                      // If date is today, validate time is not in the past
                      if (meetingDate) {
                        const selectedDate = new Date(meetingDate);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const isToday = selectedDate.getTime() === today.getTime();

                        if (isToday && selectedTime) {
                          const [hours, minutes] = selectedTime.split(':');
                          const selectedDateTime = new Date();
                          selectedDateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
                          const now = new Date();

                          if (selectedDateTime < now) {
                            setMeetingErrors(prev => ({ ...prev, meetingTime: 'Meeting time cannot be in the past.' }));
                            return;
                          }
                        }
                      }

                      setMeetingTime(selectedTime);
                      // Clear error when user selects valid time
                      if (meetingErrors.meetingTime) {
                        setMeetingErrors(prev => ({ ...prev, meetingTime: undefined }));
                      }
                    }}
                    onBlur={() => {
                      // Validate on blur
                      if (!meetingTime) {
                        setMeetingErrors(prev => ({ ...prev, meetingTime: 'Meeting time is required.' }));
                      } else if (meetingDate) {
                        // Check if date+time combination is in the past
                        const selectedDate = new Date(meetingDate);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const isToday = selectedDate.getTime() === today.getTime();

                        if (isToday) {
                          const [hours, minutes] = meetingTime.split(':');
                          const selectedDateTime = new Date();
                          selectedDateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
                          const now = new Date();

                          if (selectedDateTime < now) {
                            setMeetingErrors(prev => ({ ...prev, meetingTime: 'Meeting time cannot be in the past.' }));
                          }
                        }
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${meetingErrors.meetingTime ? 'border-red-300' : 'border-gray-300'
                      }`}
                  />
                  {meetingErrors.meetingTime && (
                    <p className="text-sm text-red-600 mt-1">{meetingErrors.meetingTime}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={meetingDuration}
                    onChange={(e) => {
                      const selectedDuration = e.target.value;
                      setMeetingDuration(selectedDuration);
                      // Clear error when user enters duration
                      if (meetingErrors.meetingDuration) {
                        setMeetingErrors(prev => ({ ...prev, meetingDuration: undefined }));
                      }
                    }}
                    onBlur={() => {
                      // Validate on blur
                      if (!meetingDuration || !meetingDuration.trim()) {
                        setMeetingErrors(prev => ({ ...prev, meetingDuration: 'Meeting duration is required.' }));
                      } else {
                        const timePattern = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
                        if (!timePattern.test(meetingDuration.trim())) {
                          setMeetingErrors(prev => ({ ...prev, meetingDuration: 'Please enter a valid duration in HH:MM format (e.g., 00:30 for 30 minutes).' }));
                        } else {
                          const [hours, minutes] = meetingDuration.trim().split(':').map(Number);
                          const totalMinutes = hours * 60 + minutes;
                          if (totalMinutes <= 0) {
                            setMeetingErrors(prev => ({ ...prev, meetingDuration: 'Meeting duration must be greater than 0 minutes.' }));
                          } else if (totalMinutes > 480) {
                            setMeetingErrors(prev => ({ ...prev, meetingDuration: 'Meeting duration cannot exceed 8 hours (480 minutes).' }));
                          }
                        }
                      }
                    }}
                    step="60"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${meetingErrors.meetingDuration ? 'border-red-300' : 'border-gray-300'
                      }`}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter duration in HH:MM format (e.g., 00:30 for 30 minutes, 01:00 for 1 hour)
                  </p>
                  {meetingErrors.meetingDuration && (
                    <p className="text-sm text-red-600 mt-1">{meetingErrors.meetingDuration}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meeting Link <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={meetingLink}
                  onChange={(e) => {
                    // Only remove dangerous HTML characters, allow all URL characters
                    const sanitized = e.target.value.replace(/[<>]/g, '');
                    setMeetingLink(sanitized.slice(0, 500));
                    // Clear error when user types
                    if (meetingErrors.meetingLink) {
                      setMeetingErrors(prev => ({ ...prev, meetingLink: undefined }));
                    }
                  }}
                  onBlur={() => {
                    // Validate on blur
                    if (!meetingLink.trim()) {
                      setMeetingErrors(prev => ({ ...prev, meetingLink: 'Meeting link is required.' }));
                    } else {
                      // Normalize URL - add https:// if no protocol is present
                      let normalizedLink = meetingLink.trim();
                      if (!normalizedLink.match(/^https?:\/\//i)) {
                        normalizedLink = `https://${normalizedLink}`;
                      }
                      // Validate URL format
                      try {
                        new URL(normalizedLink);
                      } catch (e) {
                        setMeetingErrors(prev => ({ ...prev, meetingLink: 'Please enter a valid meeting link URL.' }));
                      }
                    }
                  }}
                  placeholder="meet.google.com/roq-mxzg-rpw or https://zoom.us/j/..."
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${meetingErrors.meetingLink ? 'border-red-300' : 'border-gray-300'
                    }`}
                  maxLength={500}
                />
                <div className="flex justify-between items-center mt-1">
                  {meetingErrors.meetingLink && (
                    <p className="text-sm text-red-600">{meetingErrors.meetingLink}</p>
                  )}
                  <p className="text-xs text-gray-500 ml-auto">
                    Enter the video conferencing link (Zoom, Google Meet, Teams, etc.) - protocol optional
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  rows={4}
                  value={meetingNotes}
                  onChange={(e) => {
                    const sanitized = sanitizeTextInput(e.target.value);
                    setMeetingNotes(sanitized.slice(0, FIELD_LIMITS.notes));
                    // Clear error when user types
                    if (meetingErrors.meetingNotes) {
                      setMeetingErrors(prev => ({ ...prev, meetingNotes: undefined }));
                    }
                  }}
                  onBlur={() => {
                    // Validate on blur
                    if (meetingNotes.trim().length > FIELD_LIMITS.notes) {
                      setMeetingErrors(prev => ({ ...prev, meetingNotes: `Notes cannot exceed ${FIELD_LIMITS.notes} characters.` }));
                    }
                  }}
                  placeholder="Add any additional notes or agenda items..."
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${meetingErrors.meetingNotes ? 'border-red-300' : 'border-gray-300'
                    }`}
                  maxLength={FIELD_LIMITS.notes}
                />
                <div className="flex justify-between items-center mt-1">
                  {meetingErrors.meetingNotes && (
                    <p className="text-sm text-red-600">{meetingErrors.meetingNotes}</p>
                  )}
                  <span className="text-xs text-gray-500 ml-auto">
                    {getCharacterCount(meetingNotes, FIELD_LIMITS.notes)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowScheduleModal(false);
                  // Reset errors when canceling
                  setMeetingErrors({});
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                icon={<Calendar className="h-4 w-4" />}
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                onClick={handleConfirmScheduleMeeting}
                disabled={leadMutations.scheduleMeeting.isPending}
              >
                {leadMutations.scheduleMeeting.isPending ? 'Scheduling...' : 'Schedule Meeting'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadDetail360;
